export const NOT_APPLICABLE_REQUEST_TYPE = "\u2014";

const cleanText = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const stripUnsafeFilenameCharacters = (value) =>
  Array.from(String(value || ""))
    .filter((character) => character.charCodeAt(0) >= 32)
    .join("")
    .replace(/[<>:"/\\|?*]/g, "");

const parseIspFormData = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string" || value.trim() === "") return {};

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (error) {
    return {};
  }
};

export const isIspSubmission = (submission = {}) => {
  const submissionType = cleanText(submission.submissionType).toLowerCase();
  const storedLabel = cleanText(submission.ispRequestLabel);
  return (
    submissionType === "isp" ||
    submissionType === "both" ||
    Boolean(submission.ispFormData) ||
    Boolean(submission.ispFileName) ||
    Boolean(storedLabel && storedLabel !== NOT_APPLICABLE_REQUEST_TYPE) ||
    Boolean(submission.ispRequestType) ||
    Boolean(submission.ispExistingRequestType)
  );
};

/**
 * Return the compact ISP request label used by Submissions and filenames.
 * New records expose top-level fields; older records may have the values only
 * inside ispFormData. Pure Proposal rows deliberately display an em dash.
 */
export const getIspRequestTypeLabel = (submission = {}) => {
  if (!isIspSubmission(submission)) return NOT_APPLICABLE_REQUEST_TYPE;

  const storedLabel = cleanText(submission.ispRequestLabel);
  if (storedLabel) return storedLabel;

  const formData = parseIspFormData(submission.ispFormData);
  const requestType = cleanText(
    submission.ispRequestType ||
      submission.requestType ||
      formData.requestType ||
      formData.ispRequestType,
  );
  const existingType = cleanText(
    submission.ispExistingRequestType ||
      submission.existingRequestAction ||
      submission.existingRequestType ||
      formData.existingRequestAction ||
      formData.existingRequestType ||
      formData.ispExistingRequestType,
  );
  const requestTypeLower = requestType.toLowerCase();

  if (requestTypeLower === "new" || requestTypeLower === "new account") {
    return "New Account";
  }
  if (
    requestTypeLower === "existing" ||
    requestTypeLower === "account update"
  ) {
    return existingType || "Account Update";
  }
  if (!requestType && existingType) return existingType;

  return existingType || requestType || "ISP";
};

export const normalizeSubmissionRequestTypes = (submissions = []) =>
  (Array.isArray(submissions) ? submissions : []).map((submission) => ({
    ...submission,
    ispRequestLabel: getIspRequestTypeLabel(submission),
  }));

const sanitizeFilenameSegment = (value, maximumLength = 40) =>
  stripUnsafeFilenameCharacters(cleanText(value))
    .replace(/\s+/g, " ")
    .replace(/[. ]+$/g, "")
    .slice(0, maximumLength)
    .trim();

const capFilename = (filename, maximumLength = 240) => {
  if (filename.length <= maximumLength) return filename;
  const extensionMatch = filename.match(/(\.[^.]{1,10})$/);
  const extension = extensionMatch ? extensionMatch[1] : "";
  const stem = extension ? filename.slice(0, -extension.length) : filename;
  const available = maximumLength - extension.length;
  const suffixLength = Math.min(100, Math.floor(available / 2));
  const prefixLength = available - suffixLength - 3;
  return `${stem.slice(0, prefixLength).trimEnd()}...${stem.slice(
    -suffixLength,
  )}${extension}`;
};

export const resolveSubmissionFileName = (submission = {}, fileType = "") => {
  if (fileType === "isp") {
    return cleanText(submission.ispFileName || submission.FileName);
  }
  if (fileType === "proposal") {
    return cleanText(submission.proposalFileName || submission.FileName);
  }
  return cleanText(submission.FileName);
};

const isPrimaryFileAnIsp = (submission = {}) => {
  const submissionType = cleanText(submission.submissionType).toLowerCase();
  if (submissionType === "isp") return true;
  if (submissionType !== "both") return isIspSubmission(submission);

  // The alternate filename identifies which document occupies FileName.
  if (submission.proposalFileName) return true;
  if (submission.ispFileName) return false;
  return Boolean(submission.ispFormData);
};

const ensureRequestLabelInIspFilename = (filename, requestLabel) => {
  const safeLabel = sanitizeFilenameSegment(requestLabel);
  if (!safeLabel || requestLabel === NOT_APPLICABLE_REQUEST_TYPE) return filename;

  const marker = " - ISP ";
  const lowerFilename = filename.toLowerCase();
  const markerIndex = lowerFilename.lastIndexOf(marker.toLowerCase());
  if (markerIndex < 0) return filename;

  const expectedSegment = ` - ${safeLabel} - ISP `.toLowerCase();
  if (lowerFilename.includes(expectedSegment)) return filename;
  return `${filename.slice(0, markerIndex)} - ${safeLabel}${filename.slice(
    markerIndex,
  )}`;
};

/** Resolve the correct merged-row source name and produce a safe browser name. */
export const buildSubmissionDownloadFileName = (
  submission = {},
  fileType = "",
) => {
  const rawFileName = resolveSubmissionFileName(submission, fileType);
  let filename = rawFileName.split(/[\\/]/).pop() || "download";
  const userName = cleanText(submission.UserName);
  const clientNames = cleanText(submission["CLIENT NAMES"]);
  if (userName && clientNames) filename = filename.replace(userName, clientNames);

  const isIspDownload =
    fileType === "isp" ||
    (fileType !== "proposal" && isPrimaryFileAnIsp(submission));
  if (!isIspDownload) return filename;

  filename = ensureRequestLabelInIspFilename(
    filename,
    getIspRequestTypeLabel(submission),
  );
  filename = stripUnsafeFilenameCharacters(filename)
    .replace(/\s+/g, " ")
    .trim();
  return capFilename(filename || "ISP.pdf");
};
